import React from "react";
import { Link } from "react-router-dom";
import {
  Car,
  CheckCircle2,
  Send,
  FileText,
  Users,
  ArrowRight,
  Phone,
  Mail,
  MessageCircle,
  Settings,
  Images,
  Zap,
} from "lucide-react";
import { useVehicles } from "@/hooks/useVehicles";
import { leadStatusPill } from "@/data/leads";
import { useLeads } from "@/hooks/useLeads";
import { formatPrice } from "@/lib/format";

const statusPill: Record<string, string> = {
  published: "bg-green-100 text-green-700",
  sold: "bg-red-100 text-red-600",
  draft: "bg-slate-100 text-slate-500",
};

const quickActions = [
  { icon: Car, title: "Add New Vehicle", desc: "Create a new listing", to: "/admin/listings" },
  { icon: CheckCircle2, title: "Manage Visibility", desc: "Activate or hide listings", to: "/admin/listings" },
  { icon: Images, title: "Media Library", desc: "Upload vehicle images", to: "/admin/media" },
  { icon: Settings, title: "Site Settings", desc: "Update your details", to: "/admin/settings" },
];

const methodIcon: Record<string, any> = { WhatsApp: MessageCircle, Email: Mail, Phone: Phone, Form: FileText };

const AdminDashboard: React.FC = () => {
  const { vehicles } = useVehicles();
  const { leads } = useLeads();
  const stats = [
    { icon: Car, value: vehicles.length, label: "Total Listings", sub: "All vehicles", tone: "bg-blue-50 text-blue-600" },
    { icon: CheckCircle2, value: vehicles.filter((v) => v.status === "published").length, label: "Active", sub: "Live on website", tone: "bg-green-50 text-green-600" },
    { icon: Send, value: vehicles.filter((v) => v.status === "sold").length, label: "Sold", sub: "Hidden from website", tone: "bg-sky-50 text-sky-600" },
    { icon: FileText, value: vehicles.filter((v) => v.status === "draft").length, label: "Inactive", sub: "Hidden from website", tone: "bg-slate-100 text-slate-500" },
    { icon: Users, value: leads.filter((l) => l.status === "New").length, label: "New Inquiries", sub: "Awaiting response", tone: "bg-violet-50 text-violet-600" },
  ];
  return (
    <div className="space-y-6">
      <div>
        <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-slate-400">Admin Dashboard</p>
        <h1 className="text-2xl font-extrabold text-slate-900">Welcome back, Simphiwe 👋</h1>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-5">
        {stats.map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.label} className="card-soft p-5">
              <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${s.tone}`}>
                <Icon className="h-5 w-5" />
              </span>
              <div className="mt-3 text-2xl font-extrabold text-slate-900">{s.value}</div>
              <div className="text-sm font-semibold text-slate-700">{s.label}</div>
              <div className="text-xs text-slate-400">{s.sub}</div>
            </div>
          );
        })}
      </div>

      {/* Recent listings */}
      <div className="card-soft p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Car className="h-5 w-5 text-blue-600" />
            <h2 className="text-lg font-bold text-slate-900">Recent Listings</h2>
          </div>
          <Link to="/admin/listings" className="inline-flex items-center gap-1.5 text-sm font-bold text-blue-600">
            Manage All <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[560px] text-left text-sm">
            <thead>
              <tr className="border-b border-slate-100 text-xs uppercase tracking-wide text-slate-400">
                <th className="py-3 pr-3">Vehicle</th>
                <th className="py-3 pr-3">Price</th>
                <th className="py-3 pr-3">Status</th>
                <th className="py-3 pr-3">Date Added</th>
              </tr>
            </thead>
            <tbody>
              {vehicles.slice(0, 5).map((v) => (
                <tr key={v.id} className="border-b border-slate-50">
                  <td className="py-3 pr-3">
                    <div className="flex items-center gap-3">
                      <img src={v.image} alt={v.fullTitle} className="h-10 w-14 rounded-md object-cover" />
                      <div className="leading-tight">
                        <div className="font-bold text-slate-900">{v.fullTitle}</div>
                        <div className="text-xs text-slate-500">{v.variant}</div>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 pr-3 font-semibold text-slate-700">{formatPrice(v.price)}</td>
                  <td className="py-3 pr-3">
                    <span className={`rounded-full px-2.5 py-1 text-xs font-bold capitalize ${statusPill[v.status]}`}>{v.status}</span>
                  </td>
                  <td className="py-3 pr-3 text-slate-500">{new Date(v.createdAt).toLocaleDateString("en-ZA")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent leads */}
      <div className="card-soft p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-blue-600" />
            <h2 className="text-lg font-bold text-slate-900">Recent Leads</h2>
          </div>
          <Link to="/admin/leads" className="inline-flex items-center gap-1.5 text-sm font-bold text-blue-600">
            View All Leads <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="mt-4 space-y-2">
          {leads.slice(0, 4).map((l) => {
            const Icon = methodIcon[l.method];
            return (
              <div key={l.id} className="flex items-center justify-between rounded-xl border border-slate-100 px-4 py-3">
                <div className="flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-100 text-xs font-bold text-blue-700">{l.initials}</span>
                  <div className="leading-tight">
                    <div className="text-sm font-bold text-slate-900">{l.name}</div>
                    <div className="text-xs text-slate-500">{l.vehicle}</div>
                  </div>
                </div>
                <div className="hidden items-center gap-1.5 text-xs text-slate-500 sm:flex"><Icon className="h-3.5 w-3.5" /> {l.method}</div>
                <div className="hidden text-xs text-slate-400 md:block">{l.date}</div>
                <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${leadStatusPill(l.status)}`}>{l.status}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Quick actions */}
      <div className="card-soft p-5">
        <div className="flex items-center gap-2">
          <Zap className="h-5 w-5 text-blue-600" />
          <h2 className="text-lg font-bold text-slate-900">Quick Actions</h2>
        </div>
        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {quickActions.map((a) => {
            const Icon = a.icon;
            return (
              <Link key={a.title} to={a.to} className="flex items-center justify-between rounded-xl border border-slate-100 p-4 transition-colors hover:border-blue-200 hover:bg-blue-50/40">
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-600"><Icon className="h-5 w-5" /></span>
                  <div className="leading-tight">
                    <div className="text-sm font-bold text-slate-900">{a.title}</div>
                    <div className="text-xs text-slate-500">{a.desc}</div>
                  </div>
                </div>
                <ArrowRight className="h-4 w-4 text-slate-300" />
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
